import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, Plug, Unplug, XCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Section, SmtpSection, ImapSection } from "@/components/settings/SettingsSections";
import { api, formatApiError } from "@/lib/api";

const Check = ({ ok, label, error }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={`mailbox-check-${label.toLowerCase()}`}>
    {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" /> : <XCircle className="mt-0.5 h-4 w-4 text-rose-600" />}
    <div><p className="font-medium text-slate-900">{label}: {ok ? "funziona" : "non funziona"}</p>{!ok && error && <p className="text-xs text-rose-700">{error}</p>}</div>
  </div>
);

export const MailboxSection = ({ form, set, reload }) => {
  const connected = form.imap_configured || form.smtp_configured;
  const [email, setEmail] = useState(form.imap_account || form.sender_email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const emailRef = useRef(null);
  const pwRef = useRef(null);
  const isGmail = /@(gmail|googlemail)\./i.test(email);

  const connect = async () => {
    const em = (emailRef.current?.value || email).trim();
    const pw = pwRef.current?.value || password;
    if (!em) return toast.error("Inserisci l'indirizzo email della casella");
    if (!pw && !connected) return toast.error("Inserisci la password per le app (16 lettere)");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/settings/connect-mailbox", { email: em, password: pw.replace(/\s+/g, ""), sender_name: form.sender_name });
      setResult(data); setPassword("");
      toast.success(data.smtp_ok && data.imap_ok ? "Casella collegata" : "Casella collegata parzialmente: controlla i dettagli");
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/settings/test-mailbox"); setResult(data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    try { await api.delete("/settings/mailbox"); setResult(null); toast.success("Casella scollegata"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section title="Casella email dell'attività" testId="settings-mailbox-section"
      hint="Una sola casella per tutto: da qui Organico legge contratti e UNILAV e invia i cedolini. Basta indirizzo e password: i server vengono riconosciuti da soli.">
      <div className={`flex items-center gap-3 rounded-md border px-4 py-3 ${connected ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`} data-testid="mailbox-status">
        <Mail className={`h-5 w-5 ${connected ? "text-emerald-700" : "text-slate-400"}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">{connected ? `Collegata: ${form.imap_account || form.sender_email}` : "Nessuna casella collegata"}</p>
          <p className="text-xs text-slate-500">{connected ? `Lettura ${form.imap_configured ? "✓" : "✗"} · Invio cedolini ${form.smtp_configured ? "✓" : "✗"}` : "Collega la casella per attivare importazioni e invio cedolini."}</p>
        </div>
        {connected && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={test} disabled={busy} data-testid="mailbox-test-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Prova"}</Button>
            <ConfirmButton testId="mailbox-disconnect-button" size="sm" variant="ghost" title="Scollegare la casella?" description="Le password salvate verranno rimosse e l'importazione automatica disattivata. Dipendenti e documenti restano." onConfirm={disconnect}><Unplug className="h-4 w-4" /></ConfirmButton>
          </div>
        )}
      </div>
      {result && <div className="space-y-2 rounded-md border border-slate-200 p-3" data-testid="mailbox-result"><Check ok={result.imap_ok} label="Lettura" error={result.imap_error} /><Check ok={result.smtp_ok} label="Invio" error={result.smtp_error} /></div>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="mb-email">Indirizzo email</Label><Input id="mb-email" ref={emailRef} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cirocascella3.0@gmail.com" data-testid="mailbox-email-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="mb-pw">{isGmail ? "Password per le app (16 lettere)" : "Password"}</Label>
          <Input id="mb-pw" ref={pwRef} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder={connected ? "•••••••• (salvata, lascia vuoto per non cambiarla)" : ""} data-testid="mailbox-password-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="mb-name">Nome mittente (sui cedolini inviati)</Label><Input id="mb-name" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="Es. Ristorante Da Ciro" data-testid="mailbox-sender-name-input" /></div>
        <div className="flex items-end"><Button onClick={connect} disabled={busy} className="w-full sm:w-auto" data-testid="mailbox-connect-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />} {connected ? "Aggiorna collegamento" : "Collega casella"}</Button></div>
      </div>
      {isGmail && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="gmail-app-password-hint">
          Con Gmail la password normale <b>non funziona</b>: attiva la verifica in due passaggi e crea una{" "}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">password per le app</a> da incollare qui. Il collegamento con un clic (OAuth Google) non è ancora attivo.
        </p>
      )}
      <button type="button" onClick={() => setAdvanced((v) => !v)} className="inline-flex items-center gap-1 text-xs text-slate-500 underline" data-testid="mailbox-advanced-toggle">
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advanced ? "rotate-180" : ""}`} /> Impostazioni avanzate (server, cartelle, mittenti da controllare)
      </button>
      {advanced && <div className="space-y-6 border-t border-slate-100 pt-4"><SmtpSection form={form} set={set} /><ImapSection form={form} set={set} /></div>}
    </Section>
  );
};
